import axios from 'axios'
import { handleApiError } from '@/services/errorHandler'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useUserStore } from '@/stores/UserStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { EXCLUDED_ERROR_MESSAGES } from '@/constants/connections'
const apiClient = (isBatchProcessor = false, useMultipartForm = false) => {
  let baseURL = import.meta.env.VITE_API_HOST
  const userStore = useUserStore()
  const connectionStore = useConnectionStore()
  const headers = {}

  if (userStore.token) {
    headers['Authorization'] = `Bearer ${userStore.token}`
  }

  if (useMultipartForm) {
    headers['Content-Type'] = `multipart/form-data`
  }

  if (isBatchProcessor) {
    baseURL = import.meta.env.VITE_BATCH_PROCESSOR_API_HOST
  }

  const axiosClient = axios.create({
    baseURL,
    headers
  })

  axiosClient.interceptors.response.use(request => request, (error) => {
    console.log('Error details:', error);
    
    if (error.response) {
      // The request was made, and the server responded with a status code
      // that falls out of the range of 2xx
      console.log('Data:', error.response.data)
      console.log('Status:', error.response.status)
      console.log('Headers:', error.response.headers)
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in Node.js
      console.log('Request:', error.request)
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('Message:', error.message)
    }

    const shouldCaptureError = (error) => {
      const status = error.response?.status
      const message = error.response?.data.message

      // Check if this is a canceled/aborted request
      const isCanceledRequest = 
        error.code === 'ERR_CANCELED' ||
        error.name === 'CanceledError' ||
        error.name === 'AbortError' ||
        error.message === 'canceled' ||
        error.message?.toLowerCase()?.includes('canceled') ||
        String(error).toLowerCase().includes('canceled')

      // If it's a canceled request, don't capture it
      if (isCanceledRequest) {
        return false
      }

      const isExcludedStatus = [401, 406].includes(status)
      const hasExcludedMessage = EXCLUDED_ERROR_MESSAGES.some(errMsg => 
        message === errMsg || message?.includes(errMsg)
      )

      if (message?.includes('invalid api scope or token was expired')) {
        connectionStore.ebayAuthFailed = true
        userStore.unlinkToChannel('ebay')
      }

      return !isExcludedStatus && !hasExcludedMessage
    }

    // Use centralized error handler
    if (shouldCaptureError(error)) {
      handleApiError(error, {
        component: 'API Client',
      })
    }

    if ([401].includes(error.response?.status)) {
      const authData = localStorage.getItem("cdp_auth")

      if (authData) {
        localStorage.removeItem('cdp_auth')
        localStorage.removeItem("collx_user")
        sessionStorage.removeItem('cdp_auth_override_token')
      }

      window.location.href = import.meta.env.VITE_MAIN_SITE
    }

    console.log('Error:', error)
   
    return error
  })

  return axiosClient
}

const apiClientWithoutAuth = () => {
  return axios.create({
    baseURL: import.meta.env.VITE_API_HOST,
  })
}

const apiBuildTxt = () => {
  return axios.create({
    baseURL: import.meta.env.VITE_MAIN_SITE,
  })
}

const apiStrapi = async (apiUrl, queryParams, extraQuery) => {
  const strapiApi = import.meta.env.VITE_STRAPI_API_HOST
  const strapiToken = import.meta.env.VITE_STRAPI_TOKEN

  const queryString = new URLSearchParams(queryParams).toString()
  let apiUrlWithParams = `${strapiApi}${apiUrl}?${queryString}`

  if (extraQuery) {
      apiUrlWithParams = `${apiUrlWithParams}&${extraQuery}`
  }

  const requestOptions = {
      method: 'GET',
      headers: {
          'Authorization': `Bearer ${strapiToken}`,
      },
  }

  try {
      const response = await fetch(apiUrlWithParams, requestOptions)
      const responseData = await response.json()
      return responseData
  } catch (error) {
      console.error('Error StrapiApi', error)
      handleApiError(error, {
        component: 'Strapi API',
        action: 'fetch-strapi-data',
        data: { apiUrl },
      })
      throw error
  }
}

export {
  apiClient,
  apiClientWithoutAuth,
  apiStrapi,
  apiBuildTxt
}
